import { areaDims } from '../lib/area-geometry';
import { areaColor } from '../lib/area-colors';
import { notifyContent, notifyStructure, state, subscribeStructure } from '../state';
import type { Area, BaseImage, CropArea } from '../types';

interface Elements {
  container: HTMLElement;
}

const DEFAULT_BASE_ART_URL = '/images/blanco-ill-base.png';

export function mountBaseImageSection({ container }: Elements): void {
  container.classList.add('base-image-section');
  container.innerHTML = `
    <div class="base-image-header">
      <span class="base-image-title">Base image</span>
      <span class="base-image-dims" id="base-image-dims"></span>
    </div>
    <label class="base-image-dropzone" id="base-image-dropzone">
      <span class="base-image-empty">Drop or click to choose an image</span>
      <div class="base-image-canvas-wrap hidden">
        <canvas class="base-image-canvas" id="base-image-canvas"></canvas>
        <div class="base-image-overlay" id="base-image-overlay"></div>
      </div>
      <input type="file" accept="image/*" id="base-image-file-input" />
    </label>
  `;

  const dropzone = container.querySelector<HTMLLabelElement>('#base-image-dropzone')!;
  const fileInput = container.querySelector<HTMLInputElement>('#base-image-file-input')!;
  const canvas = container.querySelector<HTMLCanvasElement>('#base-image-canvas')!;
  const overlay = container.querySelector<HTMLDivElement>('#base-image-overlay')!;
  const wrap = container.querySelector<HTMLDivElement>('.base-image-canvas-wrap')!;
  const empty = container.querySelector<HTMLSpanElement>('.base-image-empty')!;
  const dims = container.querySelector<HTMLSpanElement>('#base-image-dims')!;

  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragging');
  });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragging'));
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragging');
    const file = e.dataTransfer?.files[0];
    if (file) void handleFile(file);
  });
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) void handleFile(file);
    fileInput.value = '';
  });

  async function handleFile(file: File): Promise<void> {
    if (!file.type.startsWith('image/')) return;
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    await loadBaseImageFromDataUrl(dataUrl, file.type || 'image/png');
  }

  async function loadBaseImageFromDataUrl(dataUrl: string, mimeType: string): Promise<void> {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to decode base image'));
      img.src = dataUrl;
    });
    const base: BaseImage = {
      dataUrl,
      mimeType,
      width: img.naturalWidth,
      height: img.naturalHeight,
    };
    state.baseImage = base;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.drawImage(img, 0, 0);
    wrap.classList.remove('hidden');
    empty.classList.add('hidden');
    dims.textContent = `${img.naturalWidth} × ${img.naturalHeight}`;
    clampAreasToBounds(base);
    ensureCropArea(base);
    renderOverlay();
    notifyStructure();
  }

  function ensureCropArea(base: BaseImage): void {
    const c = state.cropArea;
    if (!c || c.w <= 0 || c.h <= 0 || c.x < 0 || c.y < 0 ||
        c.x + c.w > base.width || c.y + c.h > base.height) {
      state.cropArea = defaultCropArea(base);
    }
  }

  function defaultCropArea(base: BaseImage): CropArea {
    return { x: 0, y: 0, w: base.width, h: base.height, hidden: true };
  }

  function clampAreasToBounds(base: BaseImage): void {
    for (const a of state.areas) {
      const { w, h } = areaDims(a);
      a.x = clamp(a.x, 0, Math.max(0, base.width - w));
      a.y = clamp(a.y, 0, Math.max(0, base.height - h));
    }
  }

  function renderOverlay(): void {
    overlay.innerHTML = '';
    if (!state.baseImage) return;
    if (state.cropArea && !state.cropArea.hidden) renderCropOverlay(state.cropArea);
    state.areas.forEach((area, idx) => {
      if (area.hidden) return;
      const el = document.createElement('div');
      el.className = 'base-image-selection';
      el.dataset.areaId = area.id;
      const color = areaColor(idx);
      el.style.borderColor = color;
      el.innerHTML = `<div class="base-image-selection-label" style="background:${color}">#${idx + 1}</div>`;
      positionSelectionEl(el, area);
      attachDrag(el, area);
      overlay.appendChild(el);
    });
  }

  function renderCropOverlay(crop: CropArea): void {
    const el = document.createElement('div');
    el.className = 'base-image-crop';
    el.innerHTML = `
      <div class="base-image-crop-label">Crop</div>
      <div class="base-image-crop-handle h-nw" data-crop-handle="nw"></div>
      <div class="base-image-crop-handle h-n" data-crop-handle="n"></div>
      <div class="base-image-crop-handle h-ne" data-crop-handle="ne"></div>
      <div class="base-image-crop-handle h-e" data-crop-handle="e"></div>
      <div class="base-image-crop-handle h-se" data-crop-handle="se"></div>
      <div class="base-image-crop-handle h-s" data-crop-handle="s"></div>
      <div class="base-image-crop-handle h-sw" data-crop-handle="sw"></div>
      <div class="base-image-crop-handle h-w" data-crop-handle="w"></div>
    `;
    positionCropEl(el, crop);
    attachCropDrag(el, crop);
    overlay.appendChild(el);
  }

  function positionCropEl(el: HTMLElement, crop: CropArea): void {
    if (!state.baseImage) return;
    const rect = canvas.getBoundingClientRect();
    const scale = rect.width / canvas.width;
    el.style.left = `${crop.x * scale}px`;
    el.style.top = `${crop.y * scale}px`;
    el.style.width = `${crop.w * scale}px`;
    el.style.height = `${crop.h * scale}px`;
  }

  function attachCropDrag(el: HTMLElement, crop: CropArea): void {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
    el.addEventListener('pointerdown', (e) => {
      if (!(e.target instanceof HTMLElement)) return;
      if (!state.baseImage) return;
      e.preventDefault();
      e.stopPropagation();
      // Raise to top of visual stack without reordering.
      overlay.appendChild(el);
      const handleEl = e.target.closest<HTMLElement>('[data-crop-handle]');
      const handle = handleEl?.dataset.cropHandle ?? null;
      const target = handleEl ?? el;
      target.setPointerCapture(e.pointerId);
      el.classList.add('dragging');
      const rect = canvas.getBoundingClientRect();
      const scale = rect.width / canvas.width;
      const startX = e.clientX;
      const startY = e.clientY;
      const orig = { ...crop };
      const baseW = state.baseImage.width;
      const baseH = state.baseImage.height;
      const MIN = 16;

      const onMove = (ev: PointerEvent): void => {
        const dx = (ev.clientX - startX) / scale;
        const dy = (ev.clientY - startY) / scale;
        let x = orig.x;
        let y = orig.y;
        let w = orig.w;
        let h = orig.h;
        if (handle === null) {
          x = clamp(orig.x + dx, 0, baseW - orig.w);
          y = clamp(orig.y + dy, 0, baseH - orig.h);
        } else {
          const hasW = handle.includes('w');
          const hasE = handle.includes('e');
          const hasN = handle.includes('n');
          const hasS = handle.includes('s');
          if (hasW) {
            const nx = clamp(orig.x + dx, 0, orig.x + orig.w - MIN);
            w = orig.x + orig.w - nx;
            x = nx;
          } else if (hasE) {
            w = clamp(orig.w + dx, MIN, baseW - orig.x);
          }
          if (hasN) {
            const ny = clamp(orig.y + dy, 0, orig.y + orig.h - MIN);
            h = orig.y + orig.h - ny;
            y = ny;
          } else if (hasS) {
            h = clamp(orig.h + dy, MIN, baseH - orig.y);
          }
        }
        crop.x = Math.round(x);
        crop.y = Math.round(y);
        crop.w = Math.round(w);
        crop.h = Math.round(h);
        positionCropEl(el, crop);
        updateCropCoordsSpan(crop);
      };
      const onUp = (): void => {
        try {
          target.releasePointerCapture(e.pointerId);
        } catch {}
        el.classList.remove('dragging');
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        target.removeEventListener('pointercancel', onUp);
        notifyContent();
      };
      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
      target.addEventListener('pointercancel', onUp);
    });
  }

  function positionSelectionEl(el: HTMLElement, area: Area): void {
    if (!state.baseImage) return;
    const rect = canvas.getBoundingClientRect();
    const scale = rect.width / canvas.width;
    const { w, h } = areaDims(area);
    el.style.left = `${area.x * scale}px`;
    el.style.top = `${area.y * scale}px`;
    el.style.width = `${w * scale}px`;
    el.style.height = `${h * scale}px`;
  }

  function refreshAllPositions(): void {
    overlay.querySelectorAll<HTMLElement>('.base-image-selection').forEach((el) => {
      const a = state.areas.find((x) => x.id === el.dataset.areaId);
      if (a) positionSelectionEl(el, a);
    });
    const cropEl = overlay.querySelector<HTMLElement>('.base-image-crop');
    if (cropEl && state.cropArea) positionCropEl(cropEl, state.cropArea);
  }

  function attachDrag(el: HTMLElement, area: Area): void {
    // Prevent clicks on the rectangle from bubbling to the dropzone label,
    // which would otherwise trigger the file picker.
    el.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
    el.addEventListener('pointerdown', (e) => {
      if (!state.baseImage) return;
      e.preventDefault();
      e.stopPropagation();
      // Raise to top of visual stack without reordering state.areas.
      overlay.appendChild(el);
      el.setPointerCapture(e.pointerId);
      el.classList.add('dragging');
      const rect = canvas.getBoundingClientRect();
      const scale = rect.width / canvas.width;
      const startX = e.clientX;
      const startY = e.clientY;
      const origX = area.x;
      const origY = area.y;
      const { w, h } = areaDims(area);
      const maxX = state.baseImage.width - w;
      const maxY = state.baseImage.height - h;

      const onMove = (ev: PointerEvent): void => {
        const dx = (ev.clientX - startX) / scale;
        const dy = (ev.clientY - startY) / scale;
        area.x = Math.round(clamp(origX + dx, 0, maxX));
        area.y = Math.round(clamp(origY + dy, 0, maxY));
        positionSelectionEl(el, area);
        updateCoordsSpan(area);
      };
      const onUp = (): void => {
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {}
        el.classList.remove('dragging');
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerup', onUp);
        el.removeEventListener('pointercancel', onUp);
        notifyContent();
      };
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerup', onUp);
      el.addEventListener('pointercancel', onUp);
    });
  }

  window.addEventListener('resize', refreshAllPositions);
  new ResizeObserver(refreshAllPositions).observe(canvas);

  subscribeStructure(renderOverlay);

  void loadDefault();

  async function loadDefault(): Promise<void> {
    if (state.baseImage) {
      // Already loaded (preset restored, etc.)
      canvas.width = state.baseImage.width;
      canvas.height = state.baseImage.height;
      ensureCropArea(state.baseImage);
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.drawImage(img, 0, 0);
        wrap.classList.remove('hidden');
        empty.classList.add('hidden');
        dims.textContent = `${state.baseImage!.width} × ${state.baseImage!.height}`;
        renderOverlay();
      };
      img.src = state.baseImage.dataUrl;
      return;
    }
    try {
      const res = await fetch(DEFAULT_BASE_ART_URL);
      if (!res.ok) return;
      const blob = await res.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      await loadBaseImageFromDataUrl(dataUrl, blob.type || 'image/png');
    } catch (err) {
      console.error('Default base image load failed', err);
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function updateCoordsSpan(area: Area): void {
  const row = document.querySelector<HTMLElement>(`.area-row[data-area-id="${area.id}"]`);
  if (!row) return;
  const span = row.querySelector<HTMLElement>('.area-coords');
  if (!span) return;
  const { w, h } = areaDims(area);
  span.textContent = `${area.x}, ${area.y} · ${w}×${h}`;
}

function updateCropCoordsSpan(crop: CropArea): void {
  const span = document.querySelector<HTMLElement>('.crop-area-row .area-coords');
  if (!span) return;
  span.textContent = `${crop.x}, ${crop.y} · ${crop.w}×${crop.h}`;
}
